/**
 * Basics Base Factory Utility
 *
 * Creates flat-color base components from a BaseColorScheme and pawn component.
 * Used by the basics skin. The celeste skin has its own factory due to
 * its different SVG structure (gradient-based colors).
 */

import React from 'react';
import type { PawnProps } from '../constants/types';
import type { BaseColorScheme } from './basics/constants/baseColorSchemes';
import BaseComponent from './basics/BaseComponent';

// Re-export ColoredBaseProps from the canonical location for backward compatibility
export type { ColoredBaseProps } from '../constants/types';

// Import locally for use in factory
import type { ColoredBaseProps } from '../constants/types';

/**
 * Factory function to create a memoized color-specific base component.
 * Components are created at module level for stable references across renders.
 */
export function createColoredBase(
  color: string,
  colorScheme: BaseColorScheme,
  PawnComponent: React.ComponentType<PawnProps>
) {
  const displayName = `${color.charAt(0).toUpperCase() + color.slice(1)}Base`;

  const ColoredBase = React.memo(function ColoredBase(props: ColoredBaseProps) {
    return (
      <BaseComponent
        {...props}
        colorScheme={colorScheme}
        PawnComponent={props.PawnComponent ?? PawnComponent}
      />
    );
  });

  ColoredBase.displayName = displayName;
  return ColoredBase;
}
